import { EUROPE_TIME_ZONE } from "@/features/orders-analytics/utils/timezone";

export type DeltaTone = "positive" | "negative" | "neutral";

type DeltaFormat = "number" | "currency" | "percentage" | "duration";

const numberFormatter = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
});

const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
});

const formatNumber = (value: number) => numberFormatter.format(value);

const formatCurrency = (value: number) => currencyFormatter.format(value);

const formatPercentage = (value: number) => `${formatNumber(value)}%`;

const formatDuration = (minutesValue: number) => {
    if (minutesValue >= 60) {
        const hours = minutesValue / 60;
        return `${formatNumber(hours)} h`;
    }

    return `${formatNumber(minutesValue)} min`;
};

const getDeltaTone = (value: number): DeltaTone => {
    if (value > 0) {
        return "positive";
    }

    if (value < 0) {
        return "negative";
    }

    return "neutral";
};

const formatDelta = (value: number, format: DeltaFormat) => {
    const sign = value > 0 ? "+" : "";

    if (format === "currency") {
        return `${sign}${formatCurrency(value)}`;
    }

    if (format === "percentage") {
        return `${sign}${formatPercentage(value)}`;
    }

    if (format === "duration") {
        return `${sign}${formatDuration(value)}`;
    }

    return `${sign}${formatNumber(value)}`;
};

const formatDateTime = (timestamp: number) =>
    new Intl.DateTimeFormat("en-US", {
        timeZone: EUROPE_TIME_ZONE,
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    }).format(new Date(timestamp));

const formatTimestampShort = (timestamp: number) =>
    new Intl.DateTimeFormat("en-US", {
        timeZone: EUROPE_TIME_ZONE,
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    }).format(new Date(timestamp));

export const formatters = {
    formatNumber,
    formatCurrency,
    formatPercentage,
    formatPercent: formatPercentage,
    formatDuration,
    formatMinutes: formatDuration,
    getDeltaTone,
    formatDelta,
    formatDateTime,
    formatTimestampShort,
};
